<template>
  <Teleport to="body">
    <div v-if="overlayStack.length" class="browser-page-overlay-root">
      <div class="browser-page-overlay-mask" aria-hidden="true" />
      <div
        v-for="(item, index) in overlayStack"
        :key="item.id"
        class="browser-page-overlay-layer"
        :style="{ zIndex: 100001 + index }"
      >
        <div
          class="browser-page-overlay-panel"
          :class="{
            'browser-page-overlay-panel--viewport90': item.size === 'viewport90',
          }"
          role="dialog"
          :aria-label="item.title"
        >
          <iframe
            class="browser-page-overlay-panel__frame"
            :src="item.url"
            :title="item.title"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { watch } from 'vue';
import { useBrowserPageOverlay } from '@/utils/browserPageOverlay';
import { notifyLiveRoomListRefresh } from '@/composables/useLiveRoomListPolling';

const { overlayStack } = useBrowserPageOverlay();

/** 冷却：避免连续关闭/重复触发导致列表被反复刷新。 */
const REFRESH_COOLDOWN_MS = 5000;
let lastRefreshTick = 0;

// 直播间设置弹层关闭后刷新一次列表：浏览器 iframe 弹层内 startLiveRoomStatus 的异步回调
// 可能随 iframe 销毁而丢失（如右上角关闭按钮路径），由父窗口在弹层关闭后刷新。
// 用 join 字符串做值比较（避免 .map 返回新数组引用导致 watch 频繁触发），
// 且带 5 秒冷却，保证一次关播只触发一次刷新。
watch(
  () => overlayStack.value.map((item) => item.url).join('\u0001'),
  (newJoined, oldJoined) => {
    if (oldJoined === undefined) return;
    const oldUrls = oldJoined ? oldJoined.split('\u0001') : [];
    const newUrls = newJoined ? newJoined.split('\u0001') : [];
    const removed = oldUrls.filter((url) => url && !newUrls.includes(url));
    if (!removed.some((url) => url.includes('liveRoomSetting'))) return;
    const now = Date.now();
    if (now - lastRefreshTick < REFRESH_COOLDOWN_MS) return;
    lastRefreshTick = now;
    // 延迟给 change_live_status 留出落库时间
    setTimeout(() => notifyLiveRoomListRefresh(), 1000);
  }
);
</script>

<style lang="scss" scoped>
.browser-page-overlay-root {
  position: fixed;
  inset: 0;
  z-index: 100000;
  pointer-events: none;
}

.browser-page-overlay-mask {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.55);
  pointer-events: auto;
}

.browser-page-overlay-layer {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.browser-page-overlay-panel {
  pointer-events: auto;
  width: min(1720px, 96vw);
  height: calc(100vh - 96px);
  max-height: min(96vh, 100%);
  display: flex;
  flex-direction: column;
  background: var(--bg-color);
  border-radius: var(--border-radius);
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.45);
  box-sizing: border-box;
}

.browser-page-overlay-panel--viewport90 {
  width: 90vw;
  height: 90vh;
  max-width: 100%;
  max-height: 100%;
}

.browser-page-overlay-panel__frame {
  flex: 1;
  min-height: 0;
  width: 100%;
  height: 100%;
  border: none;
  background: var(--bg-color);
}
</style>
